import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { TextField, FormControl, MenuItem, Select } from '@material-ui/core';
import T from 'components/common/text/T';
import useInput from 'lib/hooks/useInput';
import { useShallowAppSelector } from 'store/hooks';
import { AppActions, UserActions, UtilActions, FileActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import { ICountry } from 'lib/types/sharedTypes';
import { IUserProfile } from 'lib/types/userTypes';
import { icon_user_circle } from 'components/base/images';
import ImgCrop from 'components/common/images/ImgCrop';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import CustomSpan from 'components/common/text/CustomSpan';
import UserPasswordModalContainer from 'containers/user/UserPasswordModalContainer';
import cx from 'classnames';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { icon_camera } from 'components/base/images';
import useFileInput from 'lib/hooks/useFileInput';
import { validInputString } from 'lib/library';

function EmployeeProfileContainer() {
  const {
    user,
    fetchProfile: { profile: userProfile },
    fetchProfileSuccess,
    fetchCountryList: { countryList },
    fetchEmployeeImageUpload,
    fetchEmployeeImageUploadSuccess,
    updateEmployeeProfileSuccess,
    completeImageFileUploadSuccess,
    deleteEmployeeImageSuccess,
  }: {
    user: IUserProfile;
    fetchProfile: { profile: IUserProfile };
    fetchProfileSuccess: boolean;
    fetchCountryList: {
      countryList: ICountry[];
    };
    fetchEmployeeImageUpload: any;
    fetchEmployeeImageUploadSuccess: boolean;
    updateEmployeeProfileSuccess: boolean;
    deleteEmployeeImageSuccess: boolean;
    completeImageFileUploadSuccess: boolean;
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    fetchProfile: state.user.fetchProfile.data || {},
    fetchProfileSuccess: state.user.fetchProfile.success,
    fetchCountryList: state.util.countryList.data || {},
    fetchEmployeeImageUpload: state.file.employeeImageUploadUrl.data || {},
    fetchEmployeeImageUploadSuccess: state.file.employeeImageUploadUrl.success,
    updateEmployeeProfileSuccess: state.user.updateEmployeeProfile.success,
    completeImageFileUploadSuccess: state.file.completeImageFileUpload.success,
    deleteEmployeeImageSuccess: state.file.deleteEmployeeImage.success,
  }));

  const [modalType, setModalType] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModify, setIsModify] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const profileRef = useRef<null | any>(null);
  const profileFileRef = useRef<null | any>(null);
  const [profileFilePreview, setProfileFilePreview] = useState<object | any>(null);

  const profileImage = useFileInput({
    file: null,
    name: '',
  });
  const profileIdx = useInput(null);
  const deleteProfileImageIdx = useInput(null);

  const phoneCode = useInput(null);
  const phone = useInput('');
  const assignedTask = useInput('');

  const alertNotificationPopup = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 660,
      title: '',
      content: (
        <AlertModalContent
          title="Alert"
          mainText="사용할 수 없는 프로필사진입니다."
          subText="프로필사진은 10MB이하의 이미지파일(JPG, GIF,PNG only)로 파일명은 120자 이하로 제한됩니다."
        />
      ),
      onClick() {
        return;
      },
      onCancel() {
        return;
      },
    });
  };

  const changeSuccessPopup = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      title: '',
      content: <AlertModalContent title="Success" mainText="Profile change success" subText="" />,
      onClick() {
        return;
      },
      onCancel() {
        return;
      },
    });
  };

  const handleDeleteProfileImage = () => {
    console.log('HDS] handleDeleteProfileImage ___ ', profileIdx.value);
    setProfileFilePreview(null);
    profileFileRef.current.value = null;
    profileImage.setValue({});
    if (!!profileIdx.value) {
      deleteProfileImageIdx.setValue(profileIdx.value);
    }
  };

  const handleChangeProfileImage = (event: object | any) => {
    let reader = new FileReader();
    let file = event.target.files[0];
    if (!!file) {
      if (
        file.size > 10485760 ||
        file.type.split('/')[0] !== 'image' ||
        file.name.split('.')[0].length > 124
      ) {
        alertNotificationPopup();
      } else {
        reader.onloadend = () => {
          setProfileFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
        profileImage.onChange(event);
      }
    } else {
      return;
    }
  };

  const handleOpenModal = (type: string | any) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleClickModify = () => {
    setIsModify(true);
    phone.setValue(userProfile?.phone);
    phoneCode.setValue(userProfile?.phonecode);
    assignedTask.setValue(userProfile?.assignedTask);
    setIsValid(false);
  };

  const handleClickCancel = () => {
    setIsModify(false);
    phone.setValue('');
    phoneCode.setValue(null);
    assignedTask.setValue('');
    setIsValid(false);
    setProfileFilePreview(userProfile?.profileImg);
    profileImage.setValue({});
    profileIdx.setValue(null);
    deleteProfileImageIdx.setValue(null);
  };

  const handleClickSubmit = () => {
    const submitData = {
      assignedTask: assignedTask.value,
      phonecode: phoneCode.value,
      phone: phone.value,
    };
    UserActions.update_employee_profile_request(submitData);
  };

  const fileUpload = useCallback(
    async (data: any, file: any, deleteIdx: number, syncUid: string) => {
      try {
        const response = await axios({
          url: data.signedUrl,
          method: 'put',
          data: file.file,
        });

        if (response.status === 200) {
          FileActions.complete_image_file_upload_request({
            fileType: 'employee',
            data: {
              syncUid,
              profileIdx: data.profileIdx,
              isSuccess: 1,
              deleteProfileIdx: !!deleteIdx ? deleteIdx : null,
            },
          });
        }
      } catch (error) {
        FileActions.complete_image_file_upload_request({
          fileType: 'employee',
          data: {
            syncUid,
            profileIdx: data.profileIdx,
            isSuccess: 0,
          },
        });
        AppActions.show_toast({ type: 'error', message: (error as { message: string })?.message });
      }
    },
    [],
  );

  useDidUpdateEffect(() => {
    if (fetchEmployeeImageUploadSuccess) {
      fileUpload(
        fetchEmployeeImageUpload,
        profileImage.value,
        profileIdx.value,
        userProfile?.syncUid,
      );
    }
  }, [fetchEmployeeImageUploadSuccess]);

  useEffect(() => {
    UserActions.fetch_profile_request();
    UtilActions.fetch_country_list_request();
  }, []);

  useDidUpdateEffect(() => {
    if (fetchProfileSuccess) {
      console.log('HDS] userProfile _____ ', userProfile);
      setProfileFilePreview(userProfile?.profileImg);
      profileIdx.setValue(userProfile?.profileIdx);
    }
  }, [fetchProfileSuccess]);

  useDidUpdateEffect(() => {
    if (updateEmployeeProfileSuccess) {
      if (!!profileImage.value?.file) {
        const submitFile = {
          syncUid: userProfile?.syncUid,
          fileName: profileImage.value.name,
          fileSize: profileImage.value.size,
        };
        FileActions.fetch_employee_image_upload_request(submitFile);
      } else if (!profileImage.value?.file && !!deleteProfileImageIdx.value) {
        // file delete
        const submitFile = {
          syncUid: userProfile?.syncUid,
          profileIdx: profileIdx.value,
        };
        FileActions.delete_employee_image_request(submitFile);
      } else {
        changeSuccessPopup();
        UserActions.fetch_profile_request();
        setIsModify(false);
        phone.setValue('');
        phoneCode.setValue(null);
        assignedTask.setValue('');
        setIsValid(false);
        profileImage.setValue({});
        profileIdx.setValue(null);
        deleteProfileImageIdx.setValue(null);
      }
    }
  }, [updateEmployeeProfileSuccess]);

  useDidUpdateEffect(() => {
    if (completeImageFileUploadSuccess || deleteEmployeeImageSuccess) {
      UserActions.fetch_profile_request();
      setIsModify(false);
      phone.setValue('');
      phoneCode.setValue(null);
      assignedTask.setValue('');
      setIsValid(false);
      profileImage.setValue({});
      profileIdx.setValue(null);
      deleteProfileImageIdx.setValue(null);
      changeSuccessPopup();
    }
  }, [completeImageFileUploadSuccess, deleteEmployeeImageSuccess]);

  useDidUpdateEffect(() => {
    if (
      assignedTask.value !== userProfile?.assignedTask ||
      phone.value !== userProfile?.phone ||
      phoneCode.value !== userProfile?.phonecode ||
      profileFilePreview !== userProfile?.profileImg
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [assignedTask.value, phone.value, phoneCode.value, profileFilePreview]);

  const modalList = [
    {
      type: 'password',
      title: 'Password Change',
      component: <UserPasswordModalContainer onCloseModal={handleCloseModal} />,
      width: 570,
    },
  ];

  return (
    <EmployeeProfile data-component-name="EmployeeProfile" isModify={isModify}>
      {useMemo(() => {
        const findData = modalList.find(item => item.type === modalType);
        return (
          <PlainModal isOpen={isModalOpen} onClose={handleCloseModal} width={findData?.width}>
            <AppModal
              title={findData?.title}
              content={findData?.component}
              contentCardStyle={{
                padding: '0 0 28px',
                backgroundColor: 'transparent',
              }}
              isCloseIcon={true}
              onClick={handleCloseModal}
              onCancel={handleCloseModal}
              hideButton={true}
            />
          </PlainModal>
        );
      }, [isModalOpen])}

      <div className="employeeProfile__wrapper">
        <div className="employeeProfile__container detail">
          <div className="employeeProfile__thumnail_container">
            <div className="employeeProfile__thumnail_row_box image">
              <label
                htmlFor="profileImage"
                className={cx('employeeProfile__image_box', {
                  cursorPointer: !!isModify,
                })}
              >
                {!!profileFilePreview ? (
                  <ImgCrop width={130} isCircle src={profileFilePreview} alt="profile image" />
                ) : (
                  <img className="employeeProfile__image" src={icon_user_circle} />
                )}
                {!!isModify && (
                  <>
                    <div className="employeeProfile__image_edit">
                      <input
                        type="file"
                        accept=".gif,.png,.jpeg,.jpg"
                        id="profileImage"
                        name="profileimage"
                        hidden
                        ref={profileFileRef}
                        onChange={e => {
                          handleChangeProfileImage(e);
                        }}
                      />
                    </div>
                    <img className="employeeProfile__image_edit_image" src={icon_camera} />
                  </>
                )}
              </label>
              {!!isModify && (
                <MuiButton
                  className="employeeProfile__image_delete"
                  variant="outlined"
                  color="default"
                  disableElevation
                  onClick={handleDeleteProfileImage}
                >
                  <CustomSpan fontSize={11} fontWeight={400}>
                    <T>GLOBAL_RESET</T>
                  </CustomSpan>
                </MuiButton>
              )}
            </div>

            <div className="employeeProfile__thumnail_row_box text">
              <div className="employeeProfile__name">{userProfile?.name}</div>
              <div className="employeeProfile__email">{userProfile?.email}</div>
            </div>
          </div>

          <div className="employeeProfile__info_wrapper">
            <div className="employeeProfile__info_row">
              <div className="employeeProfile__info_label">
                <T>GLOBAL_AUTHORITY</T>
              </div>
              <div className="employeeProfile__info_content authority">
                {userProfile?.authority === 1 && <T>GLOBAL_MANAGER</T>}
                {userProfile?.authority === 2 && <T>GLOBAL_MEMBER</T>}
              </div>
            </div>
            <div className="employeeProfile__info_row">
              <div className="employeeProfile__info_label">
                <T>GLOBAL_POSITION</T>
              </div>

              <div className="employeeProfile__info_content postPosition">
                {userProfile?.postPosition}
              </div>
            </div>
            <div className="employeeProfile__info_row">
              <div className="employeeProfile__info_label">
                <T>GLOBAL_TASK</T>
              </div>
              <div className="employeeProfile__info_content assignedTask">
                {!isModify ? (
                  userProfile?.assignedTask
                ) : (
                  <MuiWrapper className="employeeProfile__input_box sm">
                    <TextField
                      className="radius-md employeeProfile__input assignedTask"
                      variant="outlined"
                      fullWidth
                      type="text"
                      placeholder="ex ) Task"
                      value={assignedTask.value}
                      inputProps={{
                        maxLength: 32,
                      }}
                      onChange={e => {
                        const checkText = validInputString(e.target.value.trim());
                        assignedTask.setValue(checkText);
                      }}
                    />
                  </MuiWrapper>
                )}
              </div>
            </div>
            <div className="employeeProfile__info_row">
              <div className="employeeProfile__info_label">
                <T>GLOBAL_PHONE</T>
              </div>
              <div className="employeeProfile__info_content phone">
                {!isModify ? (
                  <>
                    {!!userProfile?.phone ? (
                      <>
                        {'+' +
                          countryList?.find((item: any) => item.id === userProfile?.phonecode)
                            ?.phonecode +
                          ' )'}

                        {userProfile.phone}
                      </>
                    ) : (
                      '-'
                    )}
                  </>
                ) : (
                  <>
                    <MuiWrapper className="employeeProfile__input_box sm">
                      <FormControl variant="outlined" className="employeeProfile__phonecode_select">
                        <Select
                          MenuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                          }}
                          name="phoneCode"
                          displayEmpty
                          value={phoneCode.value}
                          renderValue={(selected: any) => {
                            return countryList?.find((item: any) => item.id === selected)
                              ?.phonecode;
                          }}
                          onChange={phoneCode.onChange}
                        >
                          {countryList?.map((item: any, index: number) => {
                            if (!item.phonecode) return null;
                            return (
                              <MenuItem value={item.id} key={index}>
                                {item.phonecode && `${item.name} | ${item.phonecode}`}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </MuiWrapper>
                    <MuiWrapper className="employeeProfile__input_box sm">
                      <TextField
                        className="radius-md employeeProfile__input phone"
                        variant="outlined"
                        fullWidth
                        type="number"
                        placeholder="ex ) 01012345678"
                        value={phone.value.trim()}
                        onChange={e => {
                          phone.setValue(e.target.value.trim());
                        }}
                      />
                    </MuiWrapper>
                  </>
                )}
              </div>
            </div>
            <div className="employeeProfile__info_row">
              <div className="employeeProfile__info_label">
                {!isModify && <T>GLOBAL_PASSWORD</T>}
              </div>
              <div className="employeeProfile__info_content password">
                {!isModify && (
                  <MuiButton
                    className="employeeProfile__password_btn"
                    variant="contained"
                    color="default"
                    disableElevation
                    onClick={() => {
                      handleOpenModal('password');
                    }}
                  >
                    <CustomSpan fontSize={11} fontWeight={400}>
                      <T>GLOBAL_RESET_PASSWORD</T>
                    </CustomSpan>
                  </MuiButton>
                )}
              </div>
            </div>
          </div>
        </div>
        {!!user.mesSerialNumber && (
          <div className="userSerial__box">
            <div className="userSerial__label">
              <T>MES_SERIAL_NO</T>
            </div>
            <div className="userSerial__contents">{user.mesSerialNumber}</div>
          </div>
        )}
      </div>
      <div className="employeeProfile__button_container">
        <div className="employeeProfile__button_box">
          {!isModify ? (
            <MuiButton
              className="employeeProfile__modify_btn"
              variant="contained"
              color="default"
              disableElevation
              onClick={() => {
                handleClickModify();
              }}
            >
              <CustomSpan fontSize={16} fontWeight={500}>
                <T>GLOBAL_MODIFY</T>
              </CustomSpan>
            </MuiButton>
          ) : (
            <>
              <MuiButton
                className="employeeProfile__cancel_btn"
                variant="outlined"
                color="default"
                disableElevation
                onClick={() => {
                  handleClickCancel();
                }}
              >
                <CustomSpan fontSize={16} fontWeight={500}>
                  <T>GLOBAL_CANCEL</T>
                </CustomSpan>
              </MuiButton>
              <MuiButton
                className="employeeProfile__submit_btn"
                variant="contained"
                color="default"
                disableElevation
                disabled={!isValid}
                onClick={() => {
                  handleClickSubmit();
                }}
              >
                <CustomSpan fontSize={16} fontWeight={500}>
                  <T>GLOBAL_SAVE</T>
                </CustomSpan>
              </MuiButton>
            </>
          )}
        </div>
      </div>
    </EmployeeProfile>
  );
}

const EmployeeProfile = styled.section<{
  isModify: boolean;
}>`
  .employeeProfile__wrapper {
    position: relative;
    box-shadow: 0px 0px 10px rgb(0 0 0 / 16%);
    padding: 30px 0;
    margin-bottom: 40px;
    border-radius: 15px;
    width: 100%;
    background-color: #ffffff;
    .employeeProfile__container {
      background-color: #ffffff;
      height: inherit;
      display: flex;
      &.modify {
        position: relative;
        justify-content: center;
      }
      .employeeProfile__thumnail_container {
        width: 330px;
        border-right: 1px solid #e1e1e1;

        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        row-gap: 40px;
        .employeeProfile__thumnail_row_box {
          position: relative;
          &.image {
            .employeeProfile__image_box {
              display: block;
              position: relative;
              width: 130px;
              height: 130px;
              border-radius: 50%;
              overflow: hidden;
              box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.16);
              &.cursorPointer {
                &:hover {
                  cursor: pointer;
                }
              }
              .employeeProfile__image {
                width: 130px;
                height: 130px;
              }
              .employeeProfile__image_edit {
                width: 130px;
                height: 130px;
                position: absolute;
                bottom: 0;
                left: 0;
                opacity: 0.4;
                background-color: #000000;
                border-radius: 0 0 40px 40px / 0 0 40px 40px;
              }
              .employeeProfile__image_edit_image {
                position: absolute;
                top: 50%;
                left: 50%;
                background-color: transparent;
                transform: translate(-50%, -50%);
              }
            }

            .employeeProfile__image_delete {
              position: absolute;
              left: 50%;
              bottom: -33px;
              transform: translate(-50%, -0);
              height: 20px;
              width: 66px;
              border-radius: 10px;
            }
          }

          &.text {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;
            row-gap: 10px;
            .employeeProfile__name {
              font-size: 20px;
              font-weight: 500;
              line-height: 26px;
              color: #222222;
            }
            .employeeProfile__email {
              font-size: 14px;
              font-weight: 200;
              line-height: 19px;
              color: #303030;
            }
          }
        }
      }

      .employeeProfile__info_wrapper {
        padding-left: 30px;
        width: calc(100% - 330px);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        row-gap: 10px;

        .employeeProfile__info_row {
          display: flex;
          align-items: center;

          .employeeProfile__info_label {
            width: 150px;
            height: 40px;
            display: flex;
            align-items: center;
            font-size: 15px;
            font-weight: 500;
            color: #222;
          }
          .employeeProfile__info_content {
            width: calc(100% - 150px);
            height: 40px;
            display: flex;
            align-items: center;
            font-size: 14px;
            font-weight: 300;
            color: #444;
            &.phone {
              display: flex;
              column-gap: 10px;
            }
            .employeeProfile__phonecode_select {
              width: 90px;
            }
            .employeeProfile__input {
              &.phone {
                width: 120px;
              }
              &.assignedTask {
                width: 220px;
              }
            }
            .employeeProfile__password_btn {
              width: 130px;
              height: 32px;
            }
          }
        }
      }
    }
  }

  .employeeProfile__button_container {
    width: 100%;
    display: flex;
    justify-content: center;
    .employeeProfile__button_box {
      .employeeProfile__modify_btn {
        width: 360px;
      }
      .employeeProfile__cancel_btn {
        width: 120px;
      }
      .employeeProfile__submit_btn {
        width: 230px;
        margin-left: 10px;
      }
    }
  }

  .userSerial__box {
    position: absolute;
    right: 50px;
    bottom: 50px;
    display: flex;
    width: 200px;
    justify-content: space-between;
    .userSerial__label {
      font-size: 14px;
      font-weight: 500;
    }
    .userSerial__contents {
      font-size: 14px;
      font-weight: 300;
      color: #444;
    }
  }
`;
export default React.memo(EmployeeProfileContainer);
